import React, { useState, useEffect } from 'react';
import { styles } from './adminStyles';
import { getAuctionTypeLabel, AUCTION_TYPE_CONFIG, ACTIVE_AUCTION_TYPES } from '../../../config/auctionTypeConfig';
import { getDisplayStatus } from '../../../config/eventStatus';
import { OUTCOME_TYPES } from '../../../config/outcomeTypes';

// Manual events only — mirrors the backend default on Event.allowedActions[].questions
// (models/Event.js) and the farmer-facing labels these replace (AuctionBiddingView.js).
const DEFAULT_MANUAL_QUESTIONS = ['Proposal Overview', 'Expected Environmental Impact', 'Evaluation & Verification Plan'];

const AuctionManagement = ({
  mode, // 'create' | 'edit'
  // --- CREATE MODE ---
  newEvent, setNewEvent, libraryActions, algorithms = [], user, clients,
  toggleNewEventAction, updateNewEventBudget, updateNewEventGrantPrice, handleCreateEvent, handleCreateWeightChange,
  updateNewEventMaxPaymentPerFarm, updateNewEventMaxContributionPercent, updateNewEventMaxPaymentPerUnit,
  updateNewEventOptionGrantPayment,
  updateNewEventQuestion, updateNewEventFundingOptional,
  handleAuctionTypeChange, events, duplicateAuctionSettings,
  // --- EDIT MODE ---
  currentEv, isClosed, isManual,
  editName, setEditName,
  editStartDate, setEditStartDate, editEndDate, setEditEndDate,
  editMaxPayout, setEditMaxPayout, editTerms, setEditTerms,
  editMinBenefitCostRatio, setEditMinBenefitCostRatio,
  editActions, setEditActions, toggleEditAction, updateEditActionBudget, updateEditActionGrantPrice,
  updateEditActionMaxPaymentPerFarm, updateEditActionMaxContributionPercent, updateEditActionMaxPaymentPerUnit,
  updateEditActionOptionGrantPayment,
  updateEditActionQuestion, updateEditActionFundingOptional,
  editEventSummary, setEditEventSummary, editEventSummaryUrl, setEditEventSummaryUrl,
  editOutcomeValues, handleEditWeightChange,
  editFundspreaderClearingMetric, setEditFundspreaderClearingMetric,
  editFundspreaderOutcomeToken, setEditFundspreaderOutcomeToken,
  hasChanges, setHasChanges, handleUpdateEventSettings, handleDiscardChanges
}) => {
  const [duplicateSourceId, setDuplicateSourceId] = useState('');
  const [showDuplicateBanner, setShowDuplicateBanner] = useState(false);

  const handleDuplicateClick = () => {
      duplicateAuctionSettings(duplicateSourceId);
      setDuplicateSourceId('');
      setShowDuplicateBanner(true);
      setTimeout(() => setShowDuplicateBanner(false), 6000);
  };

  if (mode === 'create') {
    return (
      <div style={{ width: '100%' }}>
        <h2 style={{color: '#0F172A', margin: '0 0 15px 0', ...styles.h2}}>Create Event</h2>

        {showDuplicateBanner && (
            <div style={{ backgroundColor: '#FFFBEB', color: '#92400E', border: '1px solid #F59E0B', borderRadius: '6px', padding: '12px 16px', marginBottom: '20px', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem', fontWeight: 'bold', animation: 'fadeIn 0.3s' }}>
                ✅ Duplicate settings loaded — now update the Event Name and Start/End Dates before creating.
            </div>
        )}

        {(events || []).length > 0 && (
            <div style={{ ...styles.card, width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}>
                <label style={styles.label}>Duplicate Settings from Existing Event <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <select
                        style={{...styles.select, flex: 1}}
                        value={duplicateSourceId}
                        onChange={(e) => setDuplicateSourceId(e.target.value)}
                    >
                        <option value="">-- Choose an event to copy settings from --</option>
                        {events.map(ev => (
                            <option key={ev._id} value={ev._id}>{ev.name} ({ev.status})</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        disabled={!duplicateSourceId}
                        onClick={handleDuplicateClick}
                        style={{...styles.btnPrimary, opacity: duplicateSourceId ? 1 : 0.6, cursor: duplicateSourceId ? 'pointer' : 'not-allowed'}}
                    >
                        Duplicate Settings
                    </button>
                </div>
            </div>
        )}

        <div style={{ ...styles.card, width: '100%', boxSizing: 'border-box', borderLeft: '5px solid #0D9488', animation: 'fadeIn 0.3s' }}>
            <h3 style={{marginTop:0, color: '#0F172A', ...styles.h3}}>Configure New Event</h3>
            <form onSubmit={handleCreateEvent}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px', paddingBottom:'20px', borderBottom:'1px solid #eee' }}>
                    <div style={{gridColumn: '1 / span 2'}}>
                        <label style={styles.label}>Event Type</label>
                        <div style={{display:'flex', flexWrap: 'wrap', gap:'10px', marginTop:'10px', padding:'10px', background:'#F8FAFC', borderRadius:'4px', border: '1px solid #ddd', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                            {ACTIVE_AUCTION_TYPES.map(type => (
                                <label key={type} style={{cursor:'pointer', color: '#0F172A'}}>
                                    <input type="radio" checked={newEvent.auctionType === type} onChange={() => handleAuctionTypeChange(type)} /> {AUCTION_TYPE_CONFIG[type].label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label style={styles.label}>Event Name</label>
                        <input type="text" placeholder="e.g. Summer Biodiversity Event" style={styles.input} required value={newEvent.name} onChange={(e) => setNewEvent({...newEvent, name: e.target.value})} />
                    </div>
                    <div><label style={styles.label}>Start Date</label><input type="date" style={styles.input} required value={newEvent.startDate} onChange={(e) => setNewEvent({...newEvent, startDate: e.target.value})} /></div>
                    <div><label style={styles.label}>End Date</label><input type="date" style={styles.input} required value={newEvent.endDate} onChange={(e) => setNewEvent({...newEvent, endDate: e.target.value})} /></div>

                    {user?.role === 'superadmin' && (
                        <div style={{gridColumn: '1 / span 2'}}>
                            <label style={styles.label}>Event Scope</label>
                            <div style={{display:'flex', flexWrap: 'wrap', gap:'10px', marginTop:'10px', padding:'10px', background:'#F8FAFC', borderRadius:'4px', border: '1px solid #ddd', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                                <label style={{cursor:'pointer'}}><input type="radio" checked={!newEvent.isGlobal} onChange={() => setNewEvent({...newEvent, isGlobal: false})} /> Client-Specific</label>
                                <label style={{cursor:'pointer', fontWeight: 'bold', color: '#0D9488'}}><input type="radio" checked={!!newEvent.isGlobal} onChange={() => setNewEvent({...newEvent, isGlobal: true})} /> Global (all clients)</label>
                            </div>
                            {!newEvent.isGlobal && (
                                <select
                                    style={{...styles.select, marginTop: '10px'}}
                                    value={newEvent.clientId || ''}
                                    onChange={(e) => setNewEvent({...newEvent, clientId: e.target.value})}
                                >
                                    <option value="">-- Select Client --</option>
                                    {(clients || []).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                            )}
                        </div>
                    )}

                    <div>
                        <label style={styles.label}>Max Payout per Farmer (£) <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <input type="number" placeholder="e.g. 50000" min="0" value={newEvent.maxPayoutPerFarmer} onChange={(e) => setNewEvent({...newEvent, maxPayoutPerFarmer: e.target.value})} style={styles.input} />
                    </div>
                    <div>
                        <label style={styles.label}>Minimum Benefit:Cost Ratio <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <input
                            type="number"
                            placeholder="Defaults to 1 if left blank"
                            min="0" step="0.1"
                            value={newEvent.minBenefitCostRatio ?? ''}
                            onChange={(e) => setNewEvent({...newEvent, minBenefitCostRatio: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                            style={styles.input}
                        />
                    </div>

                    <div style={{gridColumn: '1 / span 2'}}>
                        <label style={styles.label}>Terms & Conditions Link or Text <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <textarea
                            placeholder="Paste URL or full legal text here..."
                            value={newEvent.termsAndConditions}
                            onChange={(e) => setNewEvent({...newEvent, termsAndConditions: e.target.value})}
                            style={{...styles.input, height: '80px', resize: 'vertical'}}
                        />
                    </div>

                    <div style={{gridColumn: '1 / span 2'}}>
                        <label style={styles.label}>Event Summary <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <textarea
                            placeholder="A written summary of the event shown to farmers..."
                            value={newEvent.eventSummary}
                            onChange={(e) => setNewEvent({...newEvent, eventSummary: e.target.value})}
                            style={{...styles.input, height: '80px', resize: 'vertical'}}
                        />
                    </div>
                    <div style={{gridColumn: '1 / span 2'}}>
                        <label style={styles.label}>Event Summary Link <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <input
                            type="text"
                            placeholder="https://..."
                            value={newEvent.eventSummaryUrl}
                            onChange={(e) => setNewEvent({...newEvent, eventSummaryUrl: e.target.value})}
                            style={styles.input}
                        />
                    </div>

                    {newEvent.auctionType === 'competitive_fundspreader' && (
                        <div style={{gridColumn: '1 / span 2', backgroundColor: '#F8FAFC', padding: '15px', borderRadius: '4px', border: '1px solid #0D9488'}}>
                            <label style={styles.label}>Clearing Metric</label>
                            <div style={{display:'flex', flexWrap: 'wrap', gap:'10px', marginTop:'10px'}}>
                                <label style={{cursor:'pointer', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                                    <input type="radio" checked={(newEvent.fundspreaderClearingMetric || 'area') === 'area'} onChange={() => setNewEvent({...newEvent, fundspreaderClearingMetric: 'area', fundspreaderOutcomeToken: ''})} /> Equal per Area
                                </label>
                                <label style={{cursor:'pointer', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                                    <input type="radio" checked={newEvent.fundspreaderClearingMetric === 'outcome'} onChange={() => setNewEvent({...newEvent, fundspreaderClearingMetric: 'outcome'})} /> Equal per Outcome
                                </label>
                            </div>
                            {newEvent.fundspreaderClearingMetric === 'outcome' && (
                                <div style={{ marginTop: '10px' }}>
                                    <label style={{ fontSize: '0.8rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>Clear on (optional — defaults to total benefit if left blank)</label>
                                    <select
                                        style={{...styles.select, marginTop: '5px'}}
                                        value={newEvent.fundspreaderOutcomeToken || ''}
                                        onChange={(e) => setNewEvent({...newEvent, fundspreaderOutcomeToken: e.target.value})}
                                    >
                                        <option value="">-- Total Benefit --</option>
                                        {Object.keys(newEvent.outcomeValues || {}).map(name => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <h4 style={{marginBottom:'15px', color:'#0F172A', ...styles.h3}}>Select Actions & Set Budgets</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '12px', marginBottom: '25px' }}>
                {libraryActions.filter(action => newEvent.auctionType === 'manual' ? action.actionType === 'whole_farm_manual' : action.actionType !== 'whole_farm_manual').map((action) => {
                    const isSelected = newEvent.selectedActionIds[action._id]?.enabled;
                    return (
                        <div key={action._id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '10px', border: isSelected ? '1px solid #0D9488' : '1px solid #ddd', background: isSelected ? '#F8FAFC' : '#FFFFFF', borderRadius:'4px', transition:'all 0.2s', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem' }}>
                            <input type="checkbox" checked={isSelected || false} onChange={() => toggleNewEventAction(action._id)} style={{cursor:'pointer', width:'18px', height:'18px', flexShrink: 0}} />
                            <div style={{ flex: '1 1 140px', minWidth: '120px', fontWeight: isSelected?'bold':'normal', color: '#0F172A' }}>{action.name}</div>

                            {isSelected && newEvent.auctionType === 'grants_fixed' && (
                                <div style={{display:'flex', flexWrap: 'wrap', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.85rem', color:'#0D9488', fontWeight: 'bold'}}>Grant Rate £</span>
                                    <input type="number" min="0" placeholder="0" value={newEvent.selectedActionIds[action._id]?.grantPrice || ''} onChange={(e) => updateNewEventGrantPrice(action._id, e.target.value)} style={{ width: '80px', padding:'6px', borderRadius:'4px', border:'1px solid #ccc', fontFamily: '"Inter", sans-serif' }} />
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>/{action.unit}</span>
                                </div>
                            )}
                            {isSelected && newEvent.auctionType !== 'unified_vfm' && (
                                <div style={{display:'flex', flexWrap: 'wrap', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.85rem', color:'#475569'}}>Budget £</span>
                                    <input type="number" min="0" placeholder="0" value={newEvent.selectedActionIds[action._id]?.budget || ''} onChange={(e) => updateNewEventBudget(action._id, e.target.value)} style={{ width: '100px', padding:'6px', borderRadius:'4px', border:'1px solid #ccc', fontFamily: '"Inter", sans-serif' }} />
                                </div>
                            )}
                            {isSelected && (newEvent.auctionType === 'competitive_pay_as_bid' || newEvent.auctionType === 'competitive_fundspreader') && (
                                <div style={{display:'flex', flexWrap: 'wrap', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>Max £/unit</span>
                                    <input type="number" min="0" placeholder="No cap" value={newEvent.selectedActionIds[action._id]?.maxPaymentPerUnit || ''} onChange={(e) => updateNewEventMaxPaymentPerUnit(action._id, e.target.value)} style={{ width: '80px', padding:'6px', borderRadius:'4px', border:'1px solid #ccc', fontFamily: '"Inter", sans-serif' }} />
                                </div>
                            )}
                            {isSelected && (
                                <div style={{display:'flex', flexWrap: 'wrap', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>Max/Farm £</span>
                                    <input type="number" min="0" placeholder="No cap" value={newEvent.selectedActionIds[action._id]?.maxPaymentPerFarm || ''} onChange={(e) => updateNewEventMaxPaymentPerFarm(action._id, e.target.value)} style={{ width: '90px', padding:'6px', borderRadius:'4px', border:'1px solid #ccc', fontFamily: '"Inter", sans-serif' }} />
                                    {action.actionType !== 'field_based' && (
                                        <>
                                            <span style={{fontSize:'0.75rem', color:'#475569'}}>Max Contrib %</span>
                                            <input type="number" min="0" max="100" placeholder="100" value={newEvent.selectedActionIds[action._id]?.maxContributionPercent ?? ''} onChange={(e) => updateNewEventMaxContributionPercent(action._id, e.target.value)} style={{ width: '70px', padding:'6px', borderRadius:'4px', border:'1px solid #ccc', fontFamily: '"Inter", sans-serif' }} />
                                        </>
                                    )}
                                </div>
                            )}
                            {isSelected && newEvent.auctionType === 'grants_fixed' && (action.variations || []).length > 0 && (
                                <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #CBD5E1' }}>
                                    {action.variations.map(variation => (
                                        <div key={variation.variationName} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#0F172A', fontWeight: 'bold' }}>{variation.variationName}:</span>
                                            {(variation.options || []).map(option => {
                                                const optionKey = `${variation.variationName}|${option.optionName}`;
                                                return (
                                                    <div key={optionKey} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '0.75rem', color: '#475569' }}>{option.optionName} extra +£</span>
                                                        <input
                                                            type="number" min="0" placeholder="0"
                                                            value={newEvent.selectedActionIds[action._id]?.optionGrantPayments?.[optionKey] || ''}
                                                            onChange={(e) => updateNewEventOptionGrantPayment(action._id, optionKey, e.target.value)}
                                                            style={{ width: '70px', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontFamily: '"Inter", sans-serif' }}
                                                        />
                                                        <span style={{ fontSize: '0.7rem', color: '#475569' }}>/{action.unit}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isSelected && newEvent.auctionType === 'manual' && (
                                <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #CBD5E1' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#0F172A' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!newEvent.selectedActionIds[action._id]?.fundingOptional}
                                            onChange={(e) => updateNewEventFundingOptional(action._id, e.target.checked)}
                                        />
                                        Requested funding optional (e.g. EOI actions)
                                    </label>
                                    <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 'bold' }}>Whole Farm Proposal questions</span>
                                    {(newEvent.selectedActionIds[action._id]?.questions || DEFAULT_MANUAL_QUESTIONS).map((q, qIndex) => (
                                        <input
                                            key={qIndex}
                                            type="text"
                                            value={q}
                                            onChange={(e) => updateNewEventQuestion(action._id, qIndex, e.target.value)}
                                            style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontFamily: '"Inter", sans-serif', fontSize: '0.8rem' }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                </div>

                <div style={{backgroundColor:'#FFFFFF', color:'#0F172A', padding:'12px', borderRadius:'4px', marginBottom:'20px', fontSize:'0.9rem', textAlign:'center', border:'1px solid #F59E0B', fontFamily: '"Inter", sans-serif'}}>
                    Note: The Event Focus Area map will be defined after the event is created.
                </div>

                {(() => {
                    // Grouped by outcome NAME (not algorithm) — every selected action
                    // delivering e.g. "Nitrogen (kg/yr)" shares one valuation input,
                    // regardless of which algorithm computes it. See formulaEvaluator.js.
                    const activeCreateActions = libraryActions.filter(act => newEvent.selectedActionIds[act._id]?.enabled);
                    const outcomeNames = new Set();
                    activeCreateActions.forEach(act => {
                        (act.outcomes || []).forEach(o => {
                            const alg = algorithms.find(a => a._id === o.outcomeAlgorithmId);
                            if (alg?.outcomeType) outcomeNames.add(alg.outcomeType);
                        });
                    });
                    const names = OUTCOME_TYPES.filter(t => outcomeNames.has(t));

                    if (names.length === 0) return null;

                    return (
                        <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#F8FAFC', borderRadius: '4px', border: '1px solid #0D9488' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#0F172A', ...styles.h3 }}>Outcome Values (£ per unit)</h4>
                            <p style={{ fontSize: '0.8rem', color: '#475569', marginTop: 0, marginBottom: '15px', fontFamily: '"Inter", sans-serif' }}>
                                At least one outcome must have a value above £0.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                                {names.map(name => {
                                    return (
                                        <div key={name} style={{ display: 'flex', flexDirection: 'column' }}>
                                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>{name}</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>£</span>
                                                <input
                                                    type="number"
                                                    min="0" step="0.01"
                                                    value={newEvent.outcomeValues[name] ?? 0}
                                                    onChange={(e) => handleCreateWeightChange(name, parseFloat(e.target.value) || 0)}
                                                    style={{ width: '100px', padding: '8px', border: '1px solid #CBD5E1', borderRadius: '4px', fontFamily: 'monospace' }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                <button type="submit" style={styles.submitBtn}>
                    Create Event
                </button>
            </form>
        </div>
      </div>
    );
  }

  // --- EDIT MODE ---
  if (!currentEv) return null;

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  const isStartPast = currentEv.startDate && currentEv.startDate.split('T')[0] <= todayStr;

  const displayStatus = getDisplayStatus(currentEv);
  const statusBadgeStyle = {
    active: { backgroundColor: '#E6F4F1', color: '#0D9488' },
    ended: { backgroundColor: '#F59E0B', color: '#FFFFFF' },
    closed: { backgroundColor: '#0F172A', color: '#FFFFFF' },
  }[displayStatus] || { backgroundColor: '#E6F4F1', color: '#0D9488' };
  const statusBadgeText = displayStatus === 'ended' ? 'ENDED - AWAITING REVIEW' : currentEv.status.toUpperCase();

  return (
    <>
    <div style={{display:'flex', alignItems:'center', gap:'15px', backgroundColor:'#FFFFFF', padding:'12px 20px', borderRadius:'8px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', marginBottom: '20px', overflowX: 'auto', whiteSpace: 'nowrap'}}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', color: '#475569' }}>
            <span>Status:</span>
            <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', ...statusBadgeStyle }}>
                {statusBadgeText}
            </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', color: '#475569', borderLeft: '2px solid #ddd', paddingLeft: '15px' }}>
            <span>Type:</span>
            <span style={{ fontSize: '0.95rem', color: '#0F172A', fontWeight: 'bold' }}>
                {getAuctionTypeLabel(currentEv.auctionType)}
            </span>
        </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>

            <div style={styles.card}>
                <h3 style={{marginTop:0, borderBottom:'1px solid #eee', paddingBottom:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...styles.h3}}>
                    Event Settings
                    <span style={{fontSize:'0.8rem', fontWeight:'normal', color:'#475569', fontFamily: '"Inter", sans-serif'}}>
                        Last Updated: {currentEv.updatedAt ? new Date(currentEv.updatedAt).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                </h3>

                <div>
                    <label style={styles.label}>Event Name</label>
                    <input
                        type="text"
                        style={{...styles.input, backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF', color: isClosed ? '#475569' : '#0F172A'}}
                        value={editName}
                        disabled={isClosed}
                        required
                        onChange={(e) => { setEditName(e.target.value); setHasChanges(true); }}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                        <label style={styles.label}>Start Date</label>
                        <input
                            type="date"
                            style={{...styles.input, backgroundColor: (isStartPast || isClosed) ? '#F1F5F9' : '#FFFFFF', color: (isStartPast || isClosed) ? '#475569' : '#0F172A'}}
                            value={editStartDate}
                            disabled={isStartPast || isClosed}
                            onChange={(e) => { setEditStartDate(e.target.value); setHasChanges(true); }}
                        />
                    </div>

                    <div>
                        <label style={styles.label}>End Date</label>
                        <input
                            type="date"
                            min={tomorrowStr}
                            style={{...styles.input, backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF', color: isClosed ? '#475569' : '#0F172A'}}
                            value={editEndDate}
                            disabled={isClosed}
                            onChange={(e) => { setEditEndDate(e.target.value); setHasChanges(true); }}
                        />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '5px' }}>
                    <div>
                        <label style={styles.label}>Max Payout per Farmer (£)</label>
                        <input type="number" placeholder="Optional cap" min="0" value={editMaxPayout} disabled={isClosed} onChange={(e) => { setEditMaxPayout(e.target.value); setHasChanges(true); }} style={{...styles.input, backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}} />
                    </div>
                    <div>
                        <label style={styles.label}>Minimum Benefit:Cost Ratio <span style={{fontWeight:'normal', color:'#64748B'}}>(Optional)</span></label>
                        <input
                            type="number"
                            placeholder="Defaults to 1 if left blank"
                            min="0" step="0.1"
                            value={editMinBenefitCostRatio ?? ''}
                            disabled={isClosed}
                            onChange={(e) => { setEditMinBenefitCostRatio(e.target.value === '' ? '' : parseFloat(e.target.value)); setHasChanges(true); }}
                            style={{...styles.input, backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                        />
                    </div>
                </div>

                <div style={{ marginTop: '5px' }}>
                    <label style={styles.label}>Terms & Conditions</label>
                    <textarea
                        placeholder="Paste URL or full legal text here..."
                        value={editTerms}
                        disabled={isClosed}
                        onChange={(e) => { setEditTerms(e.target.value); setHasChanges(true); }}
                        style={{...styles.input, height: '60px', resize: 'vertical', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                    />
                </div>

                <div style={{ marginTop: '5px' }}>
                    <label style={styles.label}>Event Summary</label>
                    <textarea
                        placeholder="A written summary of the event shown to farmers..."
                        value={editEventSummary}
                        disabled={isClosed}
                        onChange={(e) => { setEditEventSummary(e.target.value); setHasChanges(true); }}
                        style={{...styles.input, height: '60px', resize: 'vertical', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                    />
                </div>
                <div style={{ marginTop: '5px' }}>
                    <label style={styles.label}>Event Summary Link</label>
                    <input
                        type="text"
                        placeholder="https://..."
                        value={editEventSummaryUrl}
                        disabled={isClosed}
                        onChange={(e) => { setEditEventSummaryUrl(e.target.value); setHasChanges(true); }}
                        style={{...styles.input, backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                    />
                </div>

                {(() => {
                    // Grouped by outcome NAME, mirroring the create-form grid — see
                    // formulaEvaluator.js for why valuation lives here, not per-action.
                    const outcomeNames = new Set();
                    editActions.filter(a => a.enabled).forEach(ea => {
                        const libAct = libraryActions.find(la => la._id === ea._id);
                        (libAct?.outcomes || []).forEach(o => {
                            const alg = algorithms.find(a => a._id === o.outcomeAlgorithmId);
                            if (alg?.outcomeType) outcomeNames.add(alg.outcomeType);
                        });
                    });
                    const names = OUTCOME_TYPES.filter(t => outcomeNames.has(t));

                    if (names.length === 0) return null;

                    return (
                        <div style={{ marginTop: '10px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                            <div style={{ padding: '15px', backgroundColor: '#F8FAFC', borderRadius: '4px', border: '1px solid #0D9488' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#0F172A', ...styles.h3 }}>Outcome Values (£ per unit)</h4>
                                <p style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '15px', marginTop: 0, fontFamily: '"Inter", sans-serif' }}>
                                    Set the £ value paid per unit of each environmental benefit. At least one must be above £0.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px' }}>
                                    {names.map(name => {
                                        return (
                                            <div key={name} style={{ display: 'flex', flexDirection: 'column' }}>
                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#0F172A', fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={name}>{name}</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '0.85rem', color: '#475569' }}>£</span>
                                                    <input
                                                        type="number"
                                                        min="0" step="0.01"
                                                        value={editOutcomeValues[name] ?? 0}
                                                        disabled={isClosed}
                                                        onChange={(e) => handleEditWeightChange(name, parseFloat(e.target.value) || 0)}
                                                        style={{ width: '100%', padding: '8px', border: '1px solid #CBD5E1', borderRadius: '4px', fontFamily: 'monospace', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF' }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {hasChanges && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '15px', animation: 'fadeIn 0.3s' }}>
                        <button onClick={handleUpdateEventSettings} style={{...styles.btnPrimary, flex: 1}}>
                            Save Settings
                        </button>
                        <button onClick={handleDiscardChanges} style={{...styles.btnReversed, flex: 1}}>
                            Discard Changes
                        </button>
                    </div>
                )}
            </div>

            <div style={styles.card}>
                {currentEv.auctionType === 'competitive_fundspreader' && (
                    <div style={{backgroundColor: '#F8FAFC', padding: '15px', borderRadius: '4px', border: '1px solid #0D9488', marginBottom: '20px'}}>
                        <label style={styles.label}>Clearing Metric</label>
                        <div style={{display:'flex', flexWrap: 'wrap', gap:'10px', marginTop:'10px'}}>
                            <label style={{cursor: isClosed ? 'not-allowed' : 'pointer', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                                <input type="radio" disabled={isClosed} checked={(editFundspreaderClearingMetric || 'area') === 'area'} onChange={() => { setEditFundspreaderClearingMetric('area'); setEditFundspreaderOutcomeToken(''); setHasChanges(true); }} /> Equal per Area
                            </label>
                            <label style={{cursor: isClosed ? 'not-allowed' : 'pointer', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem'}}>
                                <input type="radio" disabled={isClosed} checked={editFundspreaderClearingMetric === 'outcome'} onChange={() => { setEditFundspreaderClearingMetric('outcome'); setHasChanges(true); }} /> Equal per Outcome
                            </label>
                        </div>
                        {editFundspreaderClearingMetric === 'outcome' && (
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontSize: '0.8rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>Clear on (optional — defaults to total benefit if left blank)</label>
                                <select
                                    style={{...styles.select, marginTop: '5px'}}
                                    disabled={isClosed}
                                    value={editFundspreaderOutcomeToken || ''}
                                    onChange={(e) => { setEditFundspreaderOutcomeToken(e.target.value); setHasChanges(true); }}
                                >
                                    <option value="">-- Total Benefit --</option>
                                    {Object.keys(editOutcomeValues || {}).map(name => <option key={name} value={name}>{name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                <h3 style={{marginTop:0, borderBottom:'1px solid #eee', paddingBottom:'10px', ...styles.h3}}>
                    {currentEv.auctionType === 'unified_vfm' ? 'Enabled Actions'
                        : currentEv.auctionType === 'grants_fixed' ? 'Action Grant Rates & Budgets'
                        : currentEv.auctionType === 'grants' ? 'Action Grant Rates'
                        : 'Action Budgets'}
                </h3>
                <div style={{border:'1px solid #eee', borderRadius:'4px', maxHeight:'400px', overflowY:'auto', fontFamily: '"Inter", sans-serif'}}>
                    {editActions.map((action, index) => ({ action, index })).filter(({ action }) => currentEv.auctionType === 'manual' ? action.actionType === 'whole_farm_manual' : action.actionType !== 'whole_farm_manual').map(({ action, index }) => (
                        <div key={action._id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'15px', borderBottom:'1px solid #eee', backgroundColor: action.enabled ? '#FFFFFF' : '#F8FAFC', flexWrap: 'wrap'}}>
                            <input
                                type="checkbox"
                                checked={action.enabled}
                                disabled={isClosed}
                                onChange={() => { toggleEditAction(index); setHasChanges(true); }}
                                style={{cursor: isClosed ? 'not-allowed' : 'pointer', width: '18px', height: '18px'}}
                            />
                            <div style={{flex:1, color: action.enabled ? '#0F172A' : '#475569', fontSize: '0.95rem', fontWeight: action.enabled ? 'bold' : 'normal'}}>{action.label}</div>

                            {action.enabled && (currentEv.auctionType === 'grants' || currentEv.auctionType === 'grants_fixed') && (
                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.85rem', color: '#0D9488', fontWeight: 'bold'}}>Grant Rate £</span>
                                    <input
                                        type="number" min="0"
                                        placeholder="0"
                                        value={action.grantPrice || ''}
                                        disabled={isClosed}
                                        onChange={(e) => { updateEditActionGrantPrice(index, e.target.value); setHasChanges(true); }}
                                        style={{width:'80px', padding:'8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                                    />
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>/{action.unit}</span>
                                </div>
                            )}
                            {action.enabled && currentEv.auctionType !== 'unified_vfm' && currentEv.auctionType !== 'grants' && (
                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.85rem', color: '#475569'}}>Budget £</span>
                                    <input
                                        type="number" min="0"
                                        placeholder="0"
                                        value={action.budget || ''}
                                        disabled={isClosed}
                                        onChange={(e) => { updateEditActionBudget(index, e.target.value); setHasChanges(true); }}
                                        style={{width:'100px', padding:'8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                                    />
                                </div>
                            )}
                            {action.enabled && (currentEv.auctionType === 'competitive_pay_as_bid' || currentEv.auctionType === 'competitive_fundspreader') && (
                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>Max £/unit</span>
                                    <input
                                        type="number" min="0"
                                        placeholder="No cap"
                                        value={action.maxPaymentPerUnit || ''}
                                        disabled={isClosed}
                                        onChange={(e) => { updateEditActionMaxPaymentPerUnit(index, e.target.value); setHasChanges(true); }}
                                        style={{width:'80px', padding:'8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                                    />
                                </div>
                            )}
                            {action.enabled && (
                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                    <span style={{fontSize:'0.75rem', color:'#475569'}}>Max/Farm £</span>
                                    <input
                                        type="number" min="0"
                                        placeholder="No cap"
                                        value={action.maxPaymentPerFarm || ''}
                                        disabled={isClosed}
                                        onChange={(e) => { updateEditActionMaxPaymentPerFarm(index, e.target.value); setHasChanges(true); }}
                                        style={{width:'90px', padding:'8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                                    />
                                    {action.actionType !== 'field_based' && (
                                        <>
                                            <span style={{fontSize:'0.75rem', color:'#475569'}}>Max Contrib %</span>
                                            <input
                                                type="number" min="0" max="100"
                                                placeholder="100"
                                                value={action.maxContributionPercent ?? ''}
                                                disabled={isClosed}
                                                onChange={(e) => { updateEditActionMaxContributionPercent(index, e.target.value); setHasChanges(true); }}
                                                style={{width:'70px', padding:'8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF'}}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                            {action.enabled && (currentEv.auctionType === 'grants' || currentEv.auctionType === 'grants_fixed') && (action.variations || []).length > 0 && (
                                <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #CBD5E1' }}>
                                    {action.variations.map(variation => (
                                        <div key={variation.variationName} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#0F172A', fontWeight: 'bold' }}>{variation.variationName}:</span>
                                            {(variation.options || []).map(option => {
                                                const optionKey = `${variation.variationName}|${option.optionName}`;
                                                return (
                                                    <div key={optionKey} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '0.75rem', color: '#475569' }}>{option.optionName} extra +£</span>
                                                        <input
                                                            type="number" min="0"
                                                            placeholder="0"
                                                            value={action.optionGrantPayments?.[optionKey] || ''}
                                                            disabled={isClosed}
                                                            onChange={(e) => { updateEditActionOptionGrantPayment(index, optionKey, e.target.value); setHasChanges(true); }}
                                                            style={{ width: '70px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF' }}
                                                        />
                                                        <span style={{ fontSize: '0.7rem', color: '#475569' }}>/{action.unit}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {action.enabled && currentEv.auctionType === 'manual' && (
                                <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #CBD5E1' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#0F172A' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!action.fundingOptional}
                                            disabled={isClosed}
                                            onChange={(e) => updateEditActionFundingOptional(index, e.target.checked)}
                                        />
                                        Requested funding optional (e.g. EOI actions)
                                    </label>
                                    <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 'bold' }}>Whole Farm Proposal questions</span>
                                    {(action.questions || DEFAULT_MANUAL_QUESTIONS).map((q, qIndex) => (
                                        <input
                                            key={qIndex}
                                            type="text"
                                            value={q}
                                            disabled={isClosed}
                                            onChange={(e) => updateEditActionQuestion(index, qIndex, e.target.value)}
                                            style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: '"Inter", sans-serif', fontSize: '0.8rem', backgroundColor: isClosed ? '#F1F5F9' : '#FFFFFF' }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </>
  );
};

export const ClosureModal = ({
  showClosureModal, setShowClosureModal, currentEv, isClosed, isManual,
  liveBids,
  simLoading, runSimulation, auctionResults, setAuctionResults,
  handleSendNudge, isNudging, commitAuction, isCommitting, previewManualClose,
  closeAuctionEarly, isClosingEarly
}) => {
  const [showEarlyCloseForm, setShowEarlyCloseForm] = useState(false);
  const [earlyCloseReason, setEarlyCloseReason] = useState('');

  useEffect(() => {
    setShowEarlyCloseForm(false);
    setEarlyCloseReason('');
  }, [currentEv?._id]);

  if (!showClosureModal || !currentEv) return null;

  // Total Spend must reflect the real payout obligation, not the submitted
  // bid price — for competitive_fundspreader auctions every winner is
  // re-priced at the marginal winner's flat £/ha rate (see
  // competitiveFundspreaderStrategy.js), so summary.totalSpend.total (summed
  // from bid price before that repricing) understates true outlay. Every
  // other auction type never sets payoutAmount, so this falls back to price
  // unchanged for them.
  const totalSpend = (auctionResults?.winners || []).reduce((sum, w) => sum + (w.payoutAmount ?? w.price ?? 0), 0);

  return (
    <div style={styles.overlay}>
        <div style={{...styles.modal, padding: '30px', overflowY: 'auto'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                <h2 style={{margin: 0, color: '#0F172A', ...styles.h2}}>Closure — {currentEv.name}</h2>
                <button onClick={() => setShowClosureModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#475569', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {isClosed && (
                    <div style={{ ...styles.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', border: '1px solid #0D9488', marginBottom: '0' }}>
                        <div>
                            <h3 style={{margin: '0 0 5px 0', color: '#0F172A', ...styles.h3}}>Event Closed</h3>
                            {currentEv.closureType === 'manual_early' ? (
                                <p style={{margin: 0, color: '#475569', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif'}}>
                                    This event was closed early by an administrator — no winners/losers were assessed.
                                    <br/><strong>Reason given:</strong> {currentEv.closureReason}
                                </p>
                            ) : (
                                <p style={{margin: 0, color: '#475569', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif'}}>The event has been closed and participants notified of outcomes. Activity reports can be accessed from the Reports tab.</p>
                            )}
                        </div>
                    </div>
                )}

                {!isClosed && (
                    <div style={{ ...styles.card, border: '1px solid #DC2626', backgroundColor: '#FEF2F2', marginBottom: '0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                            <div>
                                <h3 style={{margin: '0 0 5px 0', color: '#0F172A', ...styles.h3}}>Cancel Event?</h3>
                                <p style={{margin: 0, color: '#475569', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif'}}>
                                    Ends the event immediately without running any winner/loser assessment — bids are left untouched. All bidders will be emailed the reason you enter below.
                                </p>
                            </div>
                            {!showEarlyCloseForm && (
                                <button
                                    onClick={() => setShowEarlyCloseForm(true)}
                                    style={{ ...styles.btnReversed, width: 'auto', borderColor: '#DC2626', color: '#DC2626', whiteSpace: 'nowrap' }}
                                >
                                    Cancel Event?
                                </button>
                            )}
                        </div>

                        {showEarlyCloseForm && (
                            <div style={{ marginTop: '15px' }}>
                                <textarea
                                    value={earlyCloseReason}
                                    onChange={(e) => setEarlyCloseReason(e.target.value)}
                                    placeholder="Reason for closing this event early (required) — included in the email sent to all bidders."
                                    style={{ ...styles.input, width: '100%', height: '80px', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                    <button
                                        onClick={() => { setShowEarlyCloseForm(false); setEarlyCloseReason(''); }}
                                        style={styles.btnReversed}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => closeAuctionEarly(earlyCloseReason)}
                                        disabled={!earlyCloseReason.trim() || isClosingEarly}
                                        style={{ ...styles.btnPrimary, backgroundColor: '#DC2626', opacity: (!earlyCloseReason.trim() || isClosingEarly) ? 0.6 : 1 }}
                                    >
                                        {isClosingEarly ? 'Closing...' : 'Confirm Close & Notify Bidders'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isManual && !isClosed && liveBids.some(b => b.status === 'pending' && (!b.decisionStatus || b.decisionStatus === 'pending')) && (
                    <div style={{ ...styles.card, backgroundColor: '#F8FAFC', border: '1px solid #F59E0B', marginBottom: '0' }}>
                        <p style={{margin: 0, color: '#0F172A', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif'}}>
                            ⚠️ Some Whole Farm proposals have not been decided yet. Review and Approve/Reject/Explore them in the <strong>View Bids</strong> modal before closing, otherwise undecided proposals will be treated as rejected.
                        </p>
                    </div>
                )}

                {!isManual && (
                    <div style={styles.card}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom: '15px', borderBottom: '1px solid #eee', marginBottom: '20px'}}>
                            <div>
                                <h2 style={{color: '#0F172A', margin: '0 0 5px 0', ...styles.h2}}>Event Engine</h2>
                                <p style={{margin:0, color: '#475569', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem'}}>
                                    {isClosed
                                        ? 'Run the algorithm to determine winners.'
                                        : 'Run a Simulation to illustrate the current status of bids. Then choose to either nudge farmers, dismiss results or formally commit to closure.'}
                                    <span style={{marginLeft: '15px', fontStyle: 'italic', color: '#0D9488'}}>Last Run: {currentEv.lastSimulationRun ? new Date(currentEv.lastSimulationRun).toLocaleString('en-GB') : 'Never'}</span>
                                </p>
                            </div>
                            {simLoading ? (
                                <button disabled style={{...styles.btnPrimary, backgroundColor: '#475569', width: 'auto', cursor:'wait', opacity: 0.8}}>Running Simulation...</button>
                            ) : (
                                <button onClick={runSimulation} style={{...styles.btnPrimary, width: 'auto'}} disabled={isClosed}>Run Simulation</button>
                            )}
                        </div>

                        {auctionResults && (
                        <div style={{padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '8px', border: '2px solid #0D9488'}}>
                            <h3 style={{marginTop: 0, color: '#0F172A', ...styles.h3}}>Results Preview</h3>
                            <div style={{display: 'flex', gap: '30px', marginBottom: '20px', color: '#0F172A', fontFamily: '"Inter", sans-serif'}}>
                                <div><strong>Total Bids:</strong> {auctionResults.summary.totalBids}</div>
                                <div><strong>Selected Winners:</strong> {auctionResults.summary.winningBids}</div>
                                <div><strong>Total Spend:</strong> £{totalSpend.toLocaleString('en-GB')}</div>
                            </div>
                            <h4 style={{marginBottom:'10px', color:'#0D9488', ...styles.h3}}>Projected Winners</h4>
                            <div style={{maxHeight:'250px', overflowY:'auto', background:'#F8FAFC', padding:'10px', borderRadius:'4px', marginBottom:'20px', border:'1px solid #ddd'}}>
                                 <table style={{width:'100%', fontSize:'0.9rem', borderCollapse: 'collapse'}}>
                                     <thead>
                                         <tr style={{borderBottom: '2px solid #ddd'}}>
                                             <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Farmer</th>
                                             <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Action</th>
                                             <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Price</th>
                                             <th style={{textAlign:'left', padding: '8px', color: '#0D9488', fontFamily: '"Inter", sans-serif'}}>Benefit:Cost<br/>Ratio</th>
                                             <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Outcomes Delivered</th>
                                         </tr>
                                     </thead>
                                     <tbody>
                                         {auctionResults.winners.map((w, i) => {
                                             // Outcomes Delivered shows the raw, UNWEIGHTED physical quantity per
                                             // outcome (physicalUnits) — spatial weighting only applies to the £
                                             // benefit value, not the physical totals. See formulaEvaluator.js.
                                             const outcomesDelivered = (w.outcomeBreakdown || [])
                                                 .map(o => `${o.outcomeName}: ${(o.physicalUnits || 0).toLocaleString('en-GB', {maximumFractionDigits: 1})}`)
                                                 .join(', ');

                                             return (
                                                 <tr key={i} style={{borderBottom: '1px solid #eee'}}>
                                                     <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.farmer}</td>
                                                     <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.action}</td>
                                                     <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>£{w.price.toLocaleString('en-GB')}</td>
                                                     <td style={{padding: '8px', fontWeight: 'bold', color: '#0D9488', fontFamily: '"Inter", sans-serif'}}>
                                                         {w.benefitCostRatio != null ? `${w.benefitCostRatio.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}:1` : '-'}
                                                     </td>
                                                     <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{outcomesDelivered || '-'}</td>
                                                 </tr>
                                             )
                                         })}
                                     </tbody>
                                 </table>
                            </div>

                            {auctionResults.losers && auctionResults.losers.length > 0 && (
                                <>
                                    <h4 style={{marginBottom:'10px', color:'#475569', ...styles.h3}}>Unsuccessful Bids</h4>
                                    <div style={{maxHeight:'250px', overflowY:'auto', background:'#F8FAFC', padding:'10px', borderRadius:'4px', border:'1px solid #ddd'}}>
                                        <table style={{width:'100%', fontSize:'0.9rem', borderCollapse: 'collapse'}}>
                                            <thead>
                                                <tr style={{borderBottom: '2px solid #ddd'}}>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Farmer</th>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Action</th>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Price</th>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Benefit:Cost<br/>Ratio</th>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Outcomes Delivered</th>
                                                    <th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auctionResults.losers.map((w, i) => {
                                                    const outcomesDelivered = (w.outcomeBreakdown || [])
                                                        .map(o => `${o.outcomeName}: ${(o.physicalUnits || 0).toLocaleString('en-GB', {maximumFractionDigits: 1})}`)
                                                        .join(', ');
                                                    return (
                                                        <tr key={i} style={{borderBottom: '1px solid #eee'}}>
                                                            <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.farmer}</td>
                                                            <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.action}</td>
                                                            <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>£{w.price.toLocaleString('en-GB')}</td>
                                                            <td style={{padding: '8px', fontWeight: 'bold', color: '#475569', fontFamily: '"Inter", sans-serif'}}>
                                                                {w.benefitCostRatio != null ? `${w.benefitCostRatio.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}:1` : '-'}
                                                            </td>
                                                            <td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{outcomesDelivered || '-'}</td>
                                                            <td style={{padding: '8px', color:'#475569', fontFamily: '"Inter", sans-serif'}}>{w.reason}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <div style={{borderTop:'1px solid #eee', marginTop:'20px', paddingTop:'20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <button
                                    onClick={handleSendNudge}
                                    disabled={isNudging}
                                    style={{
                                        ...styles.btnPrimary,
                                        backgroundColor: '#0D9488',
                                        color: '#F59E0B',
                                        fontWeight: 'bold',
                                        fontFamily: '"Inter", sans-serif',
                                        opacity: isNudging ? 0.6 : 1
                                    }}
                                >
                                    {isNudging ? 'Sending...' : 'Send Live Odds to Farmers'}
                                </button>
                                <div style={{display:'flex', gap:'15px'}}>
                                    <button onClick={() => setAuctionResults(null)} disabled={isCommitting} style={{...styles.btnReversed, opacity: isCommitting ? 0.6 : 1, cursor: isCommitting ? 'not-allowed' : 'pointer'}}>Dismiss Results</button>
                                    <button onClick={commitAuction} disabled={isCommitting} style={{...styles.btnPrimary, fontSize:'1rem', opacity: isCommitting ? 0.6 : 1, cursor: isCommitting ? 'wait' : 'pointer'}}>{isCommitting ? 'Committing...' : 'Commit & Close'}</button>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                )}

                {isManual && (
                    <div style={styles.card}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom: '15px', borderBottom: '1px solid #eee', marginBottom: '20px'}}>
                            <div>
                                <h2 style={{color: '#0F172A', margin: '0 0 5px 0', ...styles.h2}}>Manual Event Controls</h2>
                                <p style={{margin:0, color: '#475569', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem'}}>Review your decisions in the "View Bids" modal. When ready, click below to see a summary and finalize.</p>
                            </div>
                            <button onClick={previewManualClose} style={{...styles.btnPrimary, width:'auto'}} disabled={isClosed}>Review & Close Manual Event</button>
                        </div>

                        {auctionResults && (
                            <div style={{padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '8px', border: '2px solid #0D9488'}}>
                                <h3 style={{marginTop: 0, color: '#0F172A', ...styles.h3}}>Manual Decision Summary</h3>
                                <div style={{display: 'flex', gap: '30px', marginBottom: '20px', color: '#0F172A', fontFamily: '"Inter", sans-serif'}}>
                                    <div><strong>Total Bids:</strong> {auctionResults.summary.totalBids}</div>
                                    <div><strong>Selected Winners:</strong> {auctionResults.summary.winningBids}</div>
                                    <div><strong>Marked Explore:</strong> {auctionResults.summary.exploredBids}</div>
                                    <div><strong>Total Spend:</strong> £{totalSpend}</div>
                                </div>

                                <h4 style={{marginBottom:'10px', color:'#0D9488', ...styles.h3}}>Selected Winners</h4>
                                <div style={{maxHeight:'200px', overflowY:'auto', background:'#F8FAFC', padding:'10px', borderRadius:'4px', marginBottom:'20px', border:'1px solid #ddd'}}>
                                    <table style={{width:'100%', fontSize:'0.9rem', borderCollapse: 'collapse'}}>
                                        <thead><tr style={{borderBottom: '2px solid #ddd'}}><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Farmer</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Action</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Price</th></tr></thead>
                                        <tbody>{auctionResults.winners.map((w, i) => <tr key={i} style={{borderBottom: '1px solid #eee'}}><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.farmer}</td><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.action}</td><td style={{padding: '8px', fontWeight: 'bold', fontFamily: '"Inter", sans-serif'}}>£{w.price}</td></tr>)}</tbody>
                                    </table>
                                </div>

                                <h4 style={{marginBottom:'10px', color:'#163321', ...styles.h3}}>Marked for Exploration</h4>
                                <div style={{maxHeight:'200px', overflowY:'auto', background:'#F8FAFC', padding:'10px', borderRadius:'4px', marginBottom:'20px', border:'1px solid #ddd'}}>
                                    <table style={{width:'100%', fontSize:'0.9rem', borderCollapse: 'collapse'}}>
                                        <thead><tr style={{borderBottom: '2px solid #ddd'}}><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Farmer</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Action</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Price</th></tr></thead>
                                        <tbody>{auctionResults.explored.map((w, i) => <tr key={i} style={{borderBottom: '1px solid #eee'}}><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.farmer}</td><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.action}</td><td style={{padding: '8px', fontWeight: 'bold', fontFamily: '"Inter", sans-serif'}}>£{w.price}</td></tr>)}</tbody>
                                    </table>
                                </div>

                                <h4 style={{marginBottom:'10px', color:'#475569', ...styles.h3}}>Bids to Reject</h4>
                                <div style={{maxHeight:'200px', overflowY:'auto', background:'#F8FAFC', padding:'10px', borderRadius:'4px', border:'1px solid #ddd'}}>
                                    <table style={{width:'100%', fontSize:'0.9rem', borderCollapse: 'collapse'}}>
                                        <thead><tr style={{borderBottom: '2px solid #ddd'}}><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Farmer</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Action</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Price</th><th style={{textAlign:'left', padding: '8px', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Status</th></tr></thead>
                                        <tbody>{auctionResults.losers.map((w, i) => <tr key={i} style={{borderBottom: '1px solid #eee'}}><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.farmer}</td><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>{w.action}</td><td style={{padding: '8px', fontFamily: '"Inter", sans-serif'}}>£{w.price}</td><td style={{padding: '8px', color:'#475569', fontFamily: '"Inter", sans-serif'}}>{w.reason}</td></tr>)}</tbody>
                                    </table>
                                </div>

                                <div style={{borderTop:'1px solid #eee', marginTop:'20px', paddingTop:'20px', textAlign:'right'}}>
                                    <button onClick={commitAuction} disabled={isCommitting} style={{...styles.btnPrimary, fontSize:'1rem', opacity: isCommitting ? 0.6 : 1, cursor: isCommitting ? 'wait' : 'pointer'}}>{isCommitting ? 'Committing...' : 'Confirm: Close & Send Emails'}</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};

export default AuctionManagement;
